import type { Device, DevicePresence } from "@gooner/shared";

/**
 * The live list — what the remote's rail shows, and what the desktop's Send-to menu and
 * on-screens pill read from.
 *
 * Not `api.devices.list()` — that is every device row the owner has, including ones whose
 * screens are switched off and ones orphaned by a power cut. The rail answers "which view
 * is on which screen right now", so a screen that is not on right now does not belong on
 * it. There is no offline section, by design: nothing recognises a returning screen, so
 * an offline row is not a screen waiting to come back, it is litter.
 *
 * Absence in the presence map is NOT online. That matters most before the first snapshot
 * arrives, when the map is empty — rendering everything then and culling a beat later
 * would flash dead screens onto the wall.
 */
export function liveDevices<D extends Device>(
  devices: readonly D[],
  presence: Record<string, DevicePresence>,
): D[] {
  // Generic so a caller filtering the LISTING shape (Device + its screen's code) keeps
  // it — the filter never looks past the Device fields.
  return devices.filter((d) => presence[d.id]?.online === true);
}

/** How long a screen must stay offline before it counts as closed rather than reloading.
 *  A reload's socket gap is around a second; ten is a wide margin either side. */
export const DEAD_AFTER_MS = 10_000;

/**
 * Which screens have been gone long enough to be closed, and the clock to carry forward.
 *
 * Closing a screen destroys its device — every device, one lifetime. The obvious place
 * to do that is the screen's own `pagehide`, and it is the wrong place: `pagehide` fires
 * on a RELOAD exactly as it fires on a close, and a page being unloaded cannot tell which
 * is happening, so a beacon there would delete the device every time someone pressed F5.
 *
 * The controller can tell them apart, because it observes over TIME rather than at an
 * instant: a reload is back inside a second and a closed tab never is. So a device is
 * swept only after presence has reported it offline continuously for DEAD_AFTER_MS.
 *
 * Two rules do the real work:
 *   • coming back CLEARS the clock — otherwise a screen reloaded twice inside the window
 *     accumulates its way to deletion;
 *   • absence from the map never STARTS one — an empty first snapshot and a wall of dead
 *     screens are indistinguishable at an instant, and only one of them should be swept.
 *
 * The clock stores WHEN the outage began and carries that instant forward untouched. It
 * is not restarted on each pass: the caller re-sweeps every couple of seconds, so a clock
 * rewritten to `now` each time never reaches the threshold and nothing is ever swept —
 * a failure that passes every single-call test and deletes nothing in production.
 *
 * Pure, and returns the next clock rather than mutating one, so the whole rule is
 * checkable across simulated ticks instead of by staring at a hook.
 *
 * Known gap: a screen that loses power sends nothing AND may have no controller watching
 * it go, so its row survives. It never reaches the rail (liveDevices filters it out), and
 * reaping it server-side on `lastSeen` belongs in the protocol plan. Do not build a
 * reaper here.
 */
export function sweepDead(
  devices: readonly Device[],
  presence: Record<string, DevicePresence>,
  offlineSince: Readonly<Record<string, number>>,
  now: number,
): { dead: string[]; offlineSince: Record<string, number> } {
  const next: Record<string, number> = {};
  const dead: string[] = [];
  for (const d of devices) {
    // Explicitly offline, not merely unmentioned.
    if (presence[d.id]?.online !== false) continue;
    const since = offlineSince[d.id] ?? now;
    if (now - since >= DEAD_AFTER_MS) dead.push(d.id);
    else next[d.id] = since;
  }
  // Anything not written above is dropped: it came back, it was swept, or it left the
  // listing. All three mean its clock is meaningless now.
  return { dead, offlineSince: next };
}

/** How long the remote waits before upgrading "Checking screen…" to the full
 *  "Screen disconnected" card. Shorter than DEAD_AFTER_MS: this only changes wording,
 *  nothing is swept, and the neutral line covers the wait so a ~1s reload never flickers. */
export const LOST_NOTICE_AFTER_MS = 5_000;

/** What the remote remembers about screens going away, carried between passes. */
export interface LostScreenState {
  /** A screen was live at some point during this mount. Never reset by an outage. */
  hadLive: boolean;
  /** When the live list last became empty while the remote's own socket was up. */
  emptySince: number | null;
  /** True once the outage has outlasted LOST_NOTICE_AFTER_MS — show "Screen disconnected". */
  lost: boolean;
}

export const NO_LOST_SCREEN: LostScreenState = { hadLive: false, emptySince: null, lost: false };

/**
 * Decide whether the remote should tell its user the paired screen is gone.
 *
 * A fresh load with nothing paired never had a live screen, so there is nothing to lose.
 * A screen that comes back inside DEAD_AFTER_MS (a reload) never shows a notice, and one
 * that returns later clears it. If the remote's OWN socket is not up (`online` false) an
 * empty live list proves nothing — it is the phone that lost the screens, not the other
 * way round — so no notice, and the clock restarts once the socket is back.
 * Pure: returns the next state so it is checkable across simulated ticks.
 */
export function stepLostScreen(
  prev: LostScreenState,
  liveCount: number,
  online: boolean,
  now: number,
): LostScreenState {
  if (liveCount > 0) return { hadLive: true, emptySince: null, lost: false };
  if (!prev.hadLive) return prev;
  if (!online) return { hadLive: true, emptySince: null, lost: false };
  const since = prev.emptySince ?? now;
  return { hadLive: true, emptySince: since, lost: now - since >= LOST_NOTICE_AFTER_MS };
}

/** What the top of the remote shows in place of the bare pairing form. */
export type LostScreenView = "form" | "checking" | "card";

/**
 * Once a screen has been live this mount and the live list is empty, the bare prominent
 * form must never appear without a message: "checking" (neutral line) until the notice
 * delay passes, then "card". Derived from state + current inputs, so there is no render
 * in between where the list is empty but the state has not caught up. "form" otherwise
 * (never paired, or the phone's own socket is down — the Reconnecting line covers that).
 * Pure.
 */
export function lostScreenView(
  state: LostScreenState,
  liveCount: number,
  online: boolean,
): LostScreenView {
  if (liveCount > 0 || !state.hadLive || !online) return "form";
  return state.lost ? "card" : "checking";
}
